import { EmployeePhone } from "../models/employees/Employees";

/**
 * Helpers for the employee contact actions shown on employee fields in the
 * Work Order Details screen.
 *
 * An "employee field" is detected from the field metadata via TabWareType — the
 * WO metadata marks employee-backed value searches (Requestor / Assigned To /
 * Technician use ValueSearch__Employee; Supervisor and Next Reviewer use their
 * own employee-backed types). Contact info (email + phones) for a given
 * employee id comes from the GetDataForEmployee response.
 */

// TabWareType values whose field value is an employee id. Extend as needed.
export const EMPLOYEE_FIELD_TABWARE_TYPES = [
  "ValueSearch__Employee",
  "ValueSearch__Supervisor",
  "ValueSearch__WorkOrderReviewer",
];

/** True when a WO Details field's value is an employee (by metadata TabWareType). */
export const isEmployeeField = (field: { TabWareType?: string | null }): boolean =>
  !!field?.TabWareType && EMPLOYEE_FIELD_TABWARE_TYPES.includes(field.TabWareType);

/** Employee email from a GetDataForEmployee response, or undefined. */
export const getEmailFromEmployeeData = (data: any): string | undefined => {
  const email = data?.EmailAddress;
  return email ? String(email).trim() : undefined;
};

/** Employee display name from a GetDataForEmployee response, or undefined. */
export const getEmployeeNameFromData = (data: any): string | undefined => {
  const name = data?.EmployeeName;
  return name ? String(name).trim() : undefined;
};

/**
 * Employee photo as a usable image src from a GetDataForEmployee response.
 * The backend nests the photo under an Image object
 * ({ EmployeeImage, AltText, ImageExtension } — maps to em_employee_image),
 * and already prefixes EmployeeImage with a data: URI. Pre-formed data:/http
 * URIs are used as-is, otherwise a data URI is built from the raw base64 using
 * ImageExtension. Returns undefined when no photo exists (caller shows a
 * default avatar/initials placeholder).
 */
export const getPhotoFromEmployeeData = (data: any): string | undefined => {
  // GetDataForEmployee returns the photo under data.Image.EmployeeImage; keep
  // the flatter shapes as fallbacks in case the response is reshaped upstream.
  const img = data?.Image;
  const raw =
    img?.EmployeeImage ?? data?.EmployeeImage ?? data?.ImageData ?? data?.Image;
  if (!raw || typeof raw !== "string") return undefined;
  if (/^(data:|https?:)/i.test(raw)) return raw;
  const ext =
    (img?.ImageExtension ?? data?.ImageExtension
      ? String(img?.ImageExtension ?? data?.ImageExtension)
      : "png")
      .replace(/^\./, "")
      .toLowerCase() || "png";
  return `data:image/${ext};base64,${raw}`;
};

/** Basic email format check — used to avoid launching a broken mailto (BR-4). */
export const isValidEmail = (email?: string): boolean => {
  const value = (email ?? "").trim();
  // Index-based rather than /^[^\s@]+@[^\s@]+\.[^\s@]+$/: the two [^\s@]+ runs
  // both accept ".", so the split point is ambiguous and a non-matching input
  // backtracks super-linearly (ReDoS hotspot). Same acceptance, linear cost.
  if (!value || /\s/.test(value)) return false;
  const at = value.indexOf("@");
  // Non-empty local part and exactly one "@".
  if (at <= 0 || at !== value.lastIndexOf("@")) return false;
  const domain = value.substring(at + 1);
  // Dotted domain: a dot that is neither the first nor the last character.
  // Search from index 1 so a leading dot does not mask a later valid one
  // (the regex allowed "." inside its first [^\s@]+ run, e.g. "a@..b").
  const dot = domain.indexOf(".", 1);
  return dot > 0 && dot < domain.length - 1;
};

/** Basic phone check — needs enough digits to dial/text (BR-4). */
export const isValidPhone = (phone?: string): boolean =>
  (phone ?? "").replace(/\D/g, "").length >= 4;

/** Normalizes the Phone[] array from a GetDataForEmployee response. */
export const extractPhonesFromEmployeeData = (data: any): EmployeePhone[] => {
  const raw = Array.isArray(data?.Phone)
    ? data.Phone
    : Array.isArray(data?.Phones)
    ? data.Phones
    : [];
  return raw
    .map((p: any) => ({
      EmployeeId: p.EmployeeId,
      PhoneCode: p.PhoneCode ?? p.Code,
      PhoneNumber: p.PhoneNumber ?? p.Number,
    }))
    .filter((p: EmployeePhone) => !!p.PhoneNumber);
};

/**
 * From a list of phones, the ones that are text-message capable based on the
 * device-local list of SMS-enabled Phone Codes configured in Settings.
 * Matching is exact/case-sensitive so codes that differ only by case
 * (e.g. "Business" vs "BUSINESS") stay distinct. When no codes are
 * configured, nothing is textable.
 */
export const filterTextablePhones = (
  phones: EmployeePhone[] | undefined,
  smsCapablePhoneCodes: string[] | undefined
): EmployeePhone[] => {
  const codes = smsCapablePhoneCodes ?? [];
  if (codes.length === 0) return [];
  return (phones ?? []).filter(
    (p) => !!p.PhoneCode && codes.includes(p.PhoneCode)
  );
};

/** Digits-only version of a phone number for use in tel:/sms: URIs. */
export const sanitizePhoneForUri = (phone?: string): string =>
  (phone ?? "").replace(/[^\d+]/g, "");
